import { setupAxios } from '@carvana/core-auth';
import axios, { AxiosRequestConfig, isAxiosError } from 'axios';
import axiosRetry from 'axios-retry';

import Cookies from 'js-cookie';
import { resolveBrandFromDomain, resolveBrandFromEnvironment } from '|/brand';
import accessToken, { getCvAccessToken } from '|/helpers/access-token';
import { getRequestHeaders } from './request-headers';

declare global {
  interface Window {
    CarvanaEvents: { emitter: { emit: (_: string) => void } };
  }
}

function uuid(seed: string | null = null): string {
  const seedInt = seed === null ? null : parseInt(seed);
  return seedInt !== null
    ? (seedInt ^ ((Math.random() * 16) >> (seedInt / 4))).toString(16)
    : `10000000${-1e3}${-4e3}${-8e3}${-1e11}`.replace(/[018]/g, uuid);
}

// Domain only: the dev-panel theme cookie must not move the API origin off the proxy.
const getBaseUrl = () =>
  resolveBrandFromDomain() === 'slate' ? window.location.origin : process.env.BASE_URL;

const getTokenRefreshUrl = () => `${getBaseUrl()}${process.env.CVNA_APP_AUTH_TOKEN}`;

let isCoreAuthHandoverApplied = false;

const axiosInstance = axios.create({
  baseURL: getBaseUrl(),
  maxRedirects: 0,
  headers: getRequestHeaders()
});

axiosRetry(axiosInstance, {
  retries: 0,
  retryDelay: retryCount => retryCount * 1000
});

const fetchAuthToken = () =>
  axiosInstance
    .post(getTokenRefreshUrl(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(res => {
      return res.data;
    })
    .catch(async error => {
      const { response: { status = null } = {} } = error;

      const isStatusFourHundred = status >= 400;

      if (isStatusFourHundred) {
        await axiosInstance.post('/user-logout').catch(() => {
          for (const token of ['CVAccessToken', 'CVIdToken']) {
            Cookies.remove(token);
          }
        });
        const brand = resolveBrandFromDomain();
        if (brand === 'slate' && status >= 500) {
          window.location.href = `${getBaseUrl()}/speedbump`;
        } else if (brand === 'slate') {
          window.location.href = `${getBaseUrl()}/login?provider=SlateFusionAuth`;
        } else {
          window.location.href = `${getBaseUrl()}/purchase`;
        }
      }
    });

const fetchAndSaveToken = async () => {
  await fetchAuthToken();
  return getCvAccessToken();
};

export const refreshTokenIfMissing = async () => {
  const { cvIdToken, cvAccessToken, isValidToken } = accessToken();

  // Slate's refresh token is HttpOnly, so CVIdToken can't gate the refresh — only a usable access token can.
  if (resolveBrandFromEnvironment() === 'slate') {
    if (!isValidToken) {
      await fetchAndSaveToken();
    }
    return;
  }

  if (cvIdToken && !cvAccessToken && !isValidToken) {
    await fetchAndSaveToken();
    return;
  }
  return;
};

axiosInstance.interceptors.request.use(
  async config => {
    const originalConfig = { headers: {}, ...config };
    originalConfig.headers['correlation-id'] = uuid();
    const token = accessToken();
    const cvAccessToken = token.cvAccessToken;

    if (cvAccessToken) originalConfig.headers.Authorization = `Bearer ${cvAccessToken}`;

    if (originalConfig.method === 'get' && /MSIE \d|Trident.*rv:/.test(navigator.userAgent)) {
      // add a random query param to remove caching on ie11
      originalConfig.url += `?${new Date().getTime()}`;
    }

    return originalConfig;
  },
  error => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  async response => response,
  async error => {
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }
    const { response: { status = null } = {} } = error;

    const originalRequest = error.config as AxiosRequestConfig & {
      isRetry?: boolean;
    };
    if (!originalRequest || originalRequest.url === getTokenRefreshUrl()) {
      return Promise.reject(error);
    }

    // Once handed over, rejecting passes the 401 to core-auth's handler, which runs after this one.
    if (status === 401 && !isCoreAuthHandoverApplied) {
      if (originalRequest.isRetry) {
        throw error;
      } else {
        originalRequest.isRetry = true;
        const newToken = await fetchAndSaveToken();

        if (newToken) {
          if (originalRequest.headers === undefined) {
            originalRequest.headers = {};
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const applyCoreAuthHandover = (interceptionUrls: string[]) => {
  if (isCoreAuthHandoverApplied) return;
  // core-auth 2.11.0 bundles its own axios, so its AxiosInstance is a structurally distinct type.
  // addBearerTokenToAllRequests left at its default (true): anonymous requests aren't auth-checked,
  // so core-auth's Bearer injection running alongside the repo's own guarded injection is accepted.
  setupAxios(axiosInstance as unknown as Parameters<typeof setupAxios>[0], {
    interceptionUrls
  });
  // Last: a throw above must leave this arm's own 401 branch armed rather than disable both.
  isCoreAuthHandoverApplied = true;
};

/** Clears the latch only; core-auth's interceptors stay attached. Test seam. */
export const resetCoreAuthHandoverForTests = () => {
  isCoreAuthHandoverApplied = false;
};

export default axiosInstance;
